import React, { FC, useEffect, useState } from 'react';

import { FormikValues, useFormik } from 'formik';
import { Box, Grid } from '@mui/material';
import { DateTime } from 'luxon';

import {
  getLongTermContractAttributes,
  setLongTermContractAttributes,
} from '@pharmaplan/common';

import useGenerateContractRange from '../../../../../hooks/LongTermContract/useGenerateContractRange';
import useViewEditContract from '../../../../../hooks/workshift/useViewEditContract';
import { useAppSelector } from '../../../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../../../hooks/useAppDispatch';
import { resetDialog } from '../../../../../reducers/dialogReducer';
import genericClasses from '../../../../../theme/GenericClasses';
import { Constants } from '../../../../../helpers/Constants';
import CustomButton from '../../../../common/CustomButton';
import strings from '../../../../../localization';

import ViewEditContractTime from './ViewEditContractTime';
import ViewEditContractDate from './ViewEditContractDate';
import styles from './styles';

interface IViewEditContract {
  isUpdate?: boolean;
  openedDate?: string;
}

const ViewEditContract: FC<IViewEditContract> = ({ isUpdate, openedDate }) => {
  const dispatch = useAppDispatch();

  const [disabled, setDisabled] = useState(true);

  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const {
    startDate: savedStartDate,
    endDate: savedEndDate,
    defaultEndTime: savedDefaultEndTime,
    defaultStartTime: savedDefaultStartTime,
  } = longTermAttributes ?? {};

  const { getFormattedObj, generateTimeIntervals, intervalsInitial } = useViewEditContract();

  const handleClose = () => {
    dispatch(resetDialog());
  };

  const handleSubmit = (values: FormikValues) => {
    const { startDate, endDate, defaultStartTime, defaultEndTime } = values ?? {};
    const formattedObj = getFormattedObj(values);
    console.log({
      shiftContractAttributes: Object.values(formattedObj),
      startDate,
      endDate,
      defaultEndTime,
      defaultStartTime,
    });
    dispatch(
      setLongTermContractAttributes({
        shiftContractAttributes: Object.values(formattedObj),
        startDate,
        endDate,
        defaultEndTime,
        defaultStartTime,
      }),
    );

    handleClose();
  };

  const initialValues = {
    startDate: savedStartDate ?? null,
    endDate: savedEndDate ?? null,
    defaultStartTime:
      savedDefaultStartTime
      ?? DateTime.now().set({ hour: 9, minute: 0, second: 0 }).toJSDate(),
    defaultEndTime:
      savedDefaultEndTime
      ?? DateTime.now().set({ hour: 17, minute: 0, second: 0 }).toJSDate(),
  };

  const formik = useFormik({
    initialValues,
    onSubmit: handleSubmit,
  });

  const { getInitialShiftAttributes } = useGenerateContractRange();

  const onDateChange = (
    startDate: DateTime | string,
    endDate: DateTime | string,
  ) => {
    setDisabled(false);
    const shiftAttributes = getInitialShiftAttributes({
      startDate: startDate as DateTime,
      endDate: endDate as DateTime,
      startTime:
        DateTime.fromJSDate(formik.values.defaultStartTime as Date).toISO({
          includeOffset: false,
        }) ?? '',
      endTime:
        DateTime.fromJSDate(formik.values.defaultEndTime as Date).toISO({
          includeOffset: false,
        }) ?? '',
    });

    if (shiftAttributes?.length) {
      formik.setValues({
        startDate: formik.values.startDate,
        endDate: formik.values.endDate,
        defaultStartTime: formik.values.defaultStartTime,
        defaultEndTime: formik.values.defaultEndTime,
        ...generateTimeIntervals(shiftAttributes)(
          formik.values?.defaultStartTime as Date,
          formik.values?.defaultEndTime as Date,
        ),
      });

      dispatch(
        setLongTermContractAttributes({
          ...longTermAttributes,
          shiftContractAttributes: shiftAttributes ?? [],
        }),
      );
    }
  };

  useEffect(() => {
    if (longTermAttributes.shiftContractAttributes.length) {
      formik.setValues({
        startDate: formik.values.startDate,
        endDate: formik.values.endDate,
        defaultStartTime: formik.values.defaultStartTime,
        defaultEndTime: formik.values.defaultEndTime,
        ...intervalsInitial(longTermAttributes.shiftContractAttributes),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [longTermAttributes.shiftContractAttributes]);

  return (
    <Box sx={styles.container}>
      <Grid
        sx={styles.subContainer}
        flexDirection="row"
        columnSpacing={4}
        container
      >
        <Grid xs={12} md={4} item>
          <ViewEditContractDate
            openedDate={openedDate}
            onDateChange={onDateChange}
            formik={formik}
            isUpdate={isUpdate}
          />
        </Grid>
        <Grid xs={12} md={8} item>
          <ViewEditContractTime setDisabled={setDisabled} formik={formik} />
        </Grid>
      </Grid>
      <Box sx={[styles.buttonContainer, genericClasses.flex]}>
        <CustomButton
          label={strings.discardChanges}
          onClick={handleClose}
          variant={Constants.variant.outlined}
        />
        <CustomButton
          disabled={disabled}
          customClass={styles.saveButton}
          onClick={formik.handleSubmit}
          label={strings.saveChanges}
        />
      </Box>
    </Box>
  );
};

export default ViewEditContract;
